import { useCallback, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Bug, Lightbulb, Loader2, X, AlertCircle, CheckCircle2, ImagePlus } from 'lucide-react'
import * as Sentry from '@sentry/react'

declare class ImageCapture {
  constructor(track: MediaStreamTrack)
  grabFrame(): Promise<ImageBitmap>
}

const RATE_LIMIT_MAX = 20
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000

type UploadResponse = {
  screenshot_url?: string
}

type CaptureResult = {
  blob: Blob | null
  previewUrl: string | null
  warning: string | null
}

type ReferenceImage = { blob: Blob; previewUrl: string }
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const MAX_REFERENCES = 5

function readRecentTimestamps(now: number, rateLimitKey: string): number[] {
  try {
    const raw = localStorage.getItem(rateLimitKey)
    if (!raw) return []
    const arr = JSON.parse(raw) as number[]
    if (!Array.isArray(arr)) return []
    return arr.filter((ts) => Number.isFinite(ts) && now - ts < RATE_LIMIT_WINDOW_MS)
  } catch {
    return []
  }
}

function writeRecentTimestamps(timestamps: number[], rateLimitKey: string) {
  try {
    localStorage.setItem(rateLimitKey, JSON.stringify(timestamps))
  } catch {
    // best effort only
  }
}

function withLeadingSlash(path: string): string {
  return path.startsWith('/') ? path : `/${path}`
}

async function captureViaScreenCapture(): Promise<Blob | null> {
  if (!navigator.mediaDevices?.getDisplayMedia) return null

  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    preferCurrentTab: true,
  } as any)

  try {
    const track = stream.getVideoTracks()[0]
    if (!track) return null

    let bitmap: ImageBitmap | null = null

    if (typeof ImageCapture !== 'undefined') {
      const imageCapture = new ImageCapture(track)
      bitmap = await imageCapture.grabFrame()
    } else {
      bitmap = await new Promise<ImageBitmap | null>((resolve) => {
        const video = document.createElement('video')
        video.srcObject = stream
        video.onloadedmetadata = () => {
          video.play().then(() => {
            requestAnimationFrame(() => {
              const c = document.createElement('canvas')
              c.width = video.videoWidth
              c.height = video.videoHeight
              const cx = c.getContext('2d')
              if (cx) {
                cx.drawImage(video, 0, 0)
                createImageBitmap(c).then(resolve).catch(() => resolve(null))
              } else {
                resolve(null)
              }
            })
          }).catch(() => resolve(null))
        }
        video.onerror = () => resolve(null)
      })
    }

    track.stop()

    if (!bitmap) return null

    const maxW = 1920
    let w = bitmap.width
    let h = bitmap.height
    if (w > maxW) {
      h = Math.round(h * (maxW / w))
      w = maxW
    }

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return null
    }
    ctx.drawImage(bitmap, 0, 0, w, h)
    bitmap.close()

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.85)
    })
  } finally {
    stream.getTracks().forEach((t) => t.stop())
  }
}

async function captureViaHtml2Canvas(): Promise<Blob | null> {
  try {
    const { default: html2canvas } = await import('html2canvas-pro')
    const canvas = await html2canvas(document.body, {
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      scale: Math.min(window.devicePixelRatio, 2),
      useCORS: true,
      allowTaint: false,
      logging: false,
    })

    let w = canvas.width
    let h = canvas.height
    const maxW = 1920
    if (w > maxW) {
      h = Math.round(h * (maxW / w))
      w = maxW
    }

    const resized = document.createElement('canvas')
    resized.width = w
    resized.height = h
    const ctx = resized.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(canvas, 0, 0, w, h)

    return new Promise<Blob | null>((resolve) => {
      resized.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85)
    })
  } catch {
    return null
  }
}


export type BugReportWidgetProps = {
  apiBase: string
  reportPath?: string
  screenshotPath?: string
  rateKey?: string
  userStorageKey?: string
}

export function BugReportWidget({
  apiBase,
  reportPath = '/api/franky/reports',
  screenshotPath = '/api/franky/reports/screenshot',
  rateKey = 'bug_report',
  userStorageKey = 'user',
}: BugReportWidgetProps) {
  const normalizedApiBase = useMemo(() => apiBase.replace(/\/+$/, ''), [apiBase])
  const normalizedReportPath = useMemo(() => withLeadingSlash(reportPath), [reportPath])
  const normalizedScreenshotPath = useMemo(() => withLeadingSlash(screenshotPath), [screenshotPath])
  const rateLimitKey = useMemo(() => `${rateKey}_timestamps`, [rateKey])
  const [isOpen, setIsOpen] = useState(false)
  const [isOpening, setIsOpening] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mode, setMode] = useState<'bug' | 'feature'>('bug')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null)
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null)
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([])

  useEffect(() => {
    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
      referenceImages.forEach((img) => URL.revokeObjectURL(img.previewUrl))
    }
  }, [screenshotPreview, referenceImages])

  const resetAndClose = useCallback(() => {
    setIsOpen(false)
    setError(null)
    setWarning(null)
    setSuccess(false)
    setDescription('')
    setMode('bug')
    setScreenshotBlob(null)
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview)
      setScreenshotPreview(null)
    }
    referenceImages.forEach((img) => URL.revokeObjectURL(img.previewUrl))
    setReferenceImages([])
  }, [screenshotPreview, referenceImages])

  const captureScreenshot = useCallback(async (): Promise<CaptureResult> => {
    try {
      const blob = await captureViaScreenCapture()
      if (blob) {
        return { blob, previewUrl: URL.createObjectURL(blob), warning: null }
      }
      const fallbackBlob = await captureViaHtml2Canvas()
      if (fallbackBlob) {
        return { blob: fallbackBlob, previewUrl: URL.createObjectURL(fallbackBlob), warning: null }
      }
      return {
        blob: null,
        previewUrl: null,
        warning: 'Could not capture screenshot. You can still submit a text-only report.',
      }
    } catch (err) {
      if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        return {
          blob: null,
          previewUrl: null,
          warning: 'Screenshot permission denied. You can still submit a text-only report.',
        }
      }
      Sentry.captureException(err, { tags: { component: 'BugReportWidget' } })
      const fallbackBlob = await captureViaHtml2Canvas()
      if (fallbackBlob) {
        return { blob: fallbackBlob, previewUrl: URL.createObjectURL(fallbackBlob), warning: null }
      }
      return {
        blob: null,
        previewUrl: null,
        warning: 'Screenshot capture failed. You can still submit a text-only report.',
      }
    }
  }, [])

  const openWithCapture = useCallback(async () => {
    if (isOpen || isOpening || isSubmitting) return

    setIsOpening(true)
    setError(null)
    setWarning(null)

    let capture: CaptureResult
    try {
      capture = await Promise.race([
        captureScreenshot(),
        new Promise<never>((_, reject) =>
          window.setTimeout(() => reject(new Error('timeout')), 15000)
        ),
      ])
    } catch {
      capture = { blob: null, previewUrl: null, warning: 'Screenshot capture timed out' }
    }

    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
    setScreenshotBlob(capture.blob)
    setScreenshotPreview(capture.previewUrl)
    setWarning(capture.warning)

    setIsOpen(true)
    setIsOpening(false)
  }, [captureScreenshot, isOpen, isOpening, isSubmitting, screenshotPreview])

  const handleReferenceFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''

    if (!files.length) return
    const remaining = MAX_REFERENCES - referenceImages.length
    if (remaining <= 0) return

    const valid = files.filter(
      (f) => SUPPORTED_IMAGE_TYPES.includes(f.type) && f.size <= 5 * 1024 * 1024
    )
    const toAdd: ReferenceImage[] = valid.slice(0, remaining).map((file) => ({
      blob: file,
      previewUrl: URL.createObjectURL(file),
    }))

    if (toAdd.length > 0) {
      setReferenceImages((prev) => [...prev, ...toAdd])
    }
  }, [referenceImages.length])

  const removeReference = useCallback((index: number) => {
    setReferenceImages((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl)
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const rateLimitMessage = useMemo(() => {
    const now = Date.now()
    const timestamps = readRecentTimestamps(now, rateLimitKey)
    if (timestamps.length < RATE_LIMIT_MAX) return null

    const oldest = Math.min(...timestamps)
    const retryMs = RATE_LIMIT_WINDOW_MS - (now - oldest)
    const retryMin = Math.max(1, Math.ceil(retryMs / 60000))
    return `Rate limit reached. Please wait about ${retryMin} minute(s) and try again.`
  }, [isOpen, isSubmitting, rateLimitKey])

  const submit = useCallback(async () => {
    setError(null)
    setWarning((prev) => prev)

    const trimmed = description.trim()
    if (!trimmed) {
      setError('Please describe the issue before submitting.')
      return
    }
    if (trimmed.length < 10) {
      setError('Please provide more detail.')
      return
    }

    const now = Date.now()
    const timestamps = readRecentTimestamps(now, rateLimitKey)
    if (timestamps.length >= RATE_LIMIT_MAX) {
      const oldest = Math.min(...timestamps)
      const retryMs = RATE_LIMIT_WINDOW_MS - (now - oldest)
      const retryMin = Math.max(1, Math.ceil(retryMs / 60000))
      setError(`Rate limit reached. Please wait about ${retryMin} minute(s) and try again.`)
      return
    }

    setIsSubmitting(true)

    let screenshotUrl: string | undefined
    let referenceUrls: string[] = []

    const uploadImage = async (blob: Blob, contentType: string): Promise<string | undefined> => {
      try {
        const upload = await fetch(`${normalizedApiBase}${normalizedScreenshotPath}`, {
          method: 'POST',
          headers: { 'Content-Type': contentType },
          body: blob,
        })
        if (upload.ok) {
          const json = (await upload.json()) as UploadResponse
          return json.screenshot_url
        }
      } catch { /* best effort */ }
      return undefined
    }

    if (screenshotBlob || referenceImages.length > 0) {
      const uploads = await Promise.all([
        screenshotBlob ? uploadImage(screenshotBlob, 'image/jpeg') : Promise.resolve(undefined),
        ...referenceImages.map((img) => uploadImage(img.blob, img.blob.type || 'image/png')),
      ])
      screenshotUrl = uploads[0]
      referenceUrls = uploads.slice(1).filter(Boolean) as string[]
      if (screenshotBlob && !uploads[0]) setWarning('Screenshot upload failed. Sending text-only report.')
      const failedCount = referenceImages.length - referenceUrls.length
      if (failedCount > 0) setWarning(`${failedCount} of ${referenceImages.length} reference image(s) failed to upload.`)
    }

    const userId = (() => {
      try {
        const u = localStorage.getItem(userStorageKey)
        return u ? JSON.parse(u).id : undefined
      } catch { return undefined }
    })()

    const sentryReplayId = Sentry.getReplay()?.getReplayId?.() ?? undefined

    const context = {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${screen.width}x${screen.height}`,
      dpr: window.devicePixelRatio,
      userAgent: navigator.userAgent,
      language: navigator.language,
      referrer: document.referrer || undefined,
      online: navigator.onLine,
      timestamp: new Date().toISOString(),
      sentryReplayId,
      referenceImageUrl: referenceUrls[0],
      referenceImageUrls: referenceUrls.length > 0 ? referenceUrls : undefined,
    }

    try {
      const res = await fetch(`${normalizedApiBase}${normalizedReportPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error_message: mode === 'feature' ? `[Feature Request] ${trimmed}` : `[User Report] ${trimmed}`,
          client_context: JSON.stringify(context),
          page_url: window.location.href,
          user_id: userId,
          session_id: mode === 'feature' ? 'feature-request' : 'user-report',
          screenshot_url: screenshotUrl,
        }),
      })

      if (!res.ok) {
        throw new Error(`Request failed (${res.status})`)
      }

      writeRecentTimestamps([...timestamps, now], rateLimitKey)
      setSuccess(true)
      window.setTimeout(() => {
        resetAndClose()
      }, 1200)
    } catch {
      setError('Failed to submit report. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [description, mode, normalizedApiBase, normalizedReportPath, normalizedScreenshotPath, rateLimitKey, referenceImages, resetAndClose, screenshotBlob, userStorageKey])

  return (
    <>
      <button
        type="button"
        className="bug-report-fab"
        onClick={openWithCapture}
        aria-label="Feedback"
        title="Report a bug or suggest a feature"
        disabled={isOpening || isSubmitting}
        style={{
          position: 'fixed',
          right: '16px',
          bottom: 'max(16px, calc(env(safe-area-inset-bottom) + 12px))',
          width: 48,
          height: 48,
          borderRadius: '999px',
          border: '1px solid rgba(255,255,255,0.2)',
          background: 'rgba(19,19,31,0.8)',
          backdropFilter: 'blur(8px)',
          color: '#f5f5ff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isOpening || isSubmitting ? 'not-allowed' : 'pointer',
          zIndex: 1200,
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        }}
      >
        {isOpening ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Bug size={18} />}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              zIndex: 1300,
              padding: '16px',
            }}
            onClick={resetAndClose}
          >
            <motion.div
              initial={{ y: 32, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.18 }}
              role="dialog"
              aria-modal="true"
              aria-label="Report a bug"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(560px, 100%)',
                borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'linear-gradient(180deg, rgba(15,15,24,0.96), rgba(10,10,18,0.96))',
                backdropFilter: 'blur(10px)',
                color: '#f8f8ff',
                boxShadow: '0 24px 50px rgba(0,0,0,0.45)',
                padding: '16px',
                maxHeight: 'min(90vh, 760px)',
                overflow: 'auto',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: 3 }}>
                  {([['bug', 'Bug', Bug], ['feature', 'Idea', Lightbulb]] as const).map(([key, label, Icon]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setMode(key as 'bug' | 'feature')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '5px 10px',
                        borderRadius: 6,
                        border: 'none',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: mode === key ? 'rgba(255,255,255,0.12)' : 'transparent',
                        color: mode === key ? '#fff' : 'rgba(255,255,255,0.45)',
                      }}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={resetAndClose}
                  aria-label="Close"
                  style={{
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.75)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {screenshotPreview ? (
                <img
                  src={screenshotPreview}
                  alt="Captured screenshot preview"
                  style={{
                    width: '100%',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.12)',
                    marginBottom: 10,
                  }}
                />
              ) : (
                <div
                  style={{
                    borderRadius: 10,
                    border: '1px dashed rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.65)',
                    padding: '12px 10px',
                    marginBottom: 10,
                    fontSize: '0.85rem',
                  }}
                >
                  Screenshot unavailable for this report.
                </div>
              )}

              {referenceImages.length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 10,
                }}>
                  {referenceImages.map((img, i) => (
                    <div key={img.previewUrl} style={{ position: 'relative', width: 80, height: 80 }}>
                      <img src={img.previewUrl} alt={`Reference ${i + 1}`} style={{
                        width: 80, height: 80, objectFit: 'cover',
                        borderRadius: 8, border: '1px solid rgba(167,139,250,0.3)',
                      }} />
                      <button type="button" onClick={() => removeReference(i)} aria-label={`Remove image ${i + 1}`} style={{
                        position: 'absolute', top: -6, right: -6,
                        width: 20, height: 20, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.7)', color: '#fca5a5',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {referenceImages.length < MAX_REFERENCES && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  marginBottom: 10,
                  borderRadius: 10,
                  border: '1px dashed rgba(167,139,250,0.35)',
                  background: 'rgba(167,139,250,0.06)',
                  color: 'rgba(167,139,250,0.8)',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}>
                  <ImagePlus size={16} />
                  <span>{referenceImages.length === 0 ? 'Attach reference images' : `Add more (${MAX_REFERENCES - referenceImages.length} remaining)`}</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleReferenceFile} style={{ display: 'none' }} />
                </label>
              )}

              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder={mode === 'feature'
                  ? "What would you like to see? Describe the feature or improvement."
                  : "Describe what happened, what you expected, and steps to reproduce."}
                style={{
                  width: '100%',
                  resize: 'vertical',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.04)',
                  color: '#fff',
                  padding: '10px 12px',
                  outline: 'none',
                  fontSize: '0.92rem',
                  marginBottom: 10,
                }}
              />

              {rateLimitMessage && !error && (
                <div style={{ marginBottom: 10, color: '#fbbf24', fontSize: '0.85rem' }}>{rateLimitMessage}</div>
              )}

              {warning && (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    marginBottom: 10,
                    color: '#fbbf24',
                    fontSize: '0.85rem',
                  }}
                >
                  <AlertCircle size={16} style={{ flex: '0 0 auto', marginTop: 1 }} />
                  <span>{warning}</span>
                </div>
              )}

              {error && (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    marginBottom: 10,
                    color: '#fca5a5',
                    fontSize: '0.85rem',
                  }}
                >
                  <AlertCircle size={16} style={{ flex: '0 0 auto', marginTop: 1 }} />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    marginBottom: 10,
                    color: '#86efac',
                    fontSize: '0.9rem',
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span>{mode === 'feature' ? 'Thanks! Your idea was submitted.' : 'Thanks. Your report was submitted.'}</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => { void submit() }}
                disabled={isSubmitting || success}
                style={{
                  width: '100%',
                  padding: '11px 12px',
                  borderRadius: 10,
                  border: 'none',
                  cursor: isSubmitting || success ? 'not-allowed' : 'pointer',
                  color: '#fff',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                  opacity: isSubmitting || success ? 0.7 : 1,
                }}
              >
                {isSubmitting ? 'Submitting...' : mode === 'feature' ? 'Submit idea' : 'Submit bug report'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default BugReportWidget
